import './lib/styleInjector';

// Keep the entry as tiny as possible; lazy‑load heavy deps only when needed

type MaybeRoot = HTMLElement | null;

async function mountInto(container: HTMLElement, dev?: boolean) {
  const [reactMod, reactDomClientMod, appMod] = await Promise.all([
    import('react'),
    import('react-dom/client'),
    import('./lib/AtunwaPlayer'),
  ]);
  const React: any = (reactMod as any).default ?? (reactMod as any);
  const ReactDOMClient: any = reactDomClientMod as any;
  const AtunwaPlayer: any = (appMod as any).default ?? (appMod as any);

  if (typeof ReactDOMClient.createRoot === 'function') {
    ReactDOMClient.createRoot(container).render(React.createElement(AtunwaPlayer, { dev }));
  } else {
    // Fallback for React < 18
    const ReactDOM: any = await import('react-dom');
    ReactDOM.render(React.createElement(AtunwaPlayer, { dev }), container);
  }
}

// 1) If a root div with id="atunwa-player" exists, mount immediately (dev/demo pages)
const directRoot: MaybeRoot = typeof document !== 'undefined' ? document.getElementById('atunwa-player') as HTMLElement | null : null;
if (directRoot) {
  const params = new URLSearchParams(window.location.search);
  const dev = params.has('dev');
  void mountInto(directRoot, dev);
} else if (typeof customElements !== 'undefined') {
  // 2) Otherwise, define a lightweight web component that lazy‑loads React and the app
  class AtunwaPlayerElement extends HTMLElement {
    private _root: any | null = null;
    private _mounted = false;

    static get observedAttributes() { return ['dev']; }

    connectedCallback() {
      if (this._mounted) return;
      this._mounted = true;
      // Defer heavy import to idle time when possible
      const schedule = (fn: () => void) => (window as any).requestIdleCallback ? (window as any).requestIdleCallback(fn) : setTimeout(fn, 0);
      schedule(() => {
        const dev = this.hasAttribute('dev');
        void mountInto(this, dev);
      });
    }

    disconnectedCallback() {
      // Optionally unmount if needed
      // We'll lazy import react-dom only if mounted
      if (this._mounted) {
        import('react-dom/client').then((ReactDOMClient) => {
          try {
            // React 18 root API doesn't expose getRoot; recreate and unmount is fine
            // But to avoid double mounting, we skip unless needed.
            // There's no reliable way to get the root reference back; so ignore.
          } catch {}
        }).catch(() => {});
      }
    }
  }

  if (!customElements.get('atunwa-player')) {
    customElements.define('atunwa-player', AtunwaPlayerElement);
  }
}
